import React from 'react';
import {
  CHECKBOX, DATE, DATETIME, TEXT
} from './fieldConstants';
import {
  Column, Engagement, ResourceDetails, ResourceType, RoleAssignment, Task
} from '../../types';
import EngagementNameCell from '../../components/cells/EngagementNameCell';
import StepCell from '../../components/cells/StepCell';
import RoleCell from '../../components/cells/RoleCell';
import {useSelector} from "react-redux";
import TaskHandlerButton from "../AdminExperience/Engagements/ViewEngagement/Dashboard/TaskHandlerButton";
import toCamelCase from "../../utils/toCamelCase";

export const ROLE_ASSIGNMENTS_FIELD = 'ROLE_ASSIGNMENTS_FIELD';

export const TASK_COLUMNS: Column[] = [
  {
    attribute: 'title',
    type: TEXT,
    label: 'Title',
  },
  {
    attribute: 'description',
    type: TEXT,
    label: 'Description',
  },
  {
    attribute: 'dueDate',
    type: DATE,
    label: 'Due date',
  },
  {
    attribute: 'assignedDatetime',
    type: DATETIME,
    label: 'Assigned date',
  },
  {
    attribute: 'completedDatetime',
    type: DATETIME,
    label: 'Completed date',
  },
  {
    attribute: 'isCompleted',
    type: CHECKBOX,
    label: 'Is completed?',
  },
  {
    attribute: 'handleTask',
    type: TEXT,
    label: 'Handle task',
    // @ts-ignore
    getCell: (task: Task, ledgerProps: any) => (
      <TaskHandlerButton
        task={task}
        onSuccess={() => ledgerProps.fetchData()}
      />
    )
  },
];

const UserRoleCell = ({ roleAssignments }: { roleAssignments: RoleAssignment[] }) => {
  const account = useSelector((state) => (state as any).account);
  return <RoleCell roleAssignments={roleAssignments} accountId={account.user.id} />
}

export const ENGAGEMENT_COLUMNS = [
  {
    attribute: 'name',
    type: TEXT,
    label: 'Name',
    getCell: (engagement: Engagement) => <EngagementNameCell engagement={engagement} />
  },
  {
    attribute: 'stepName',
    type: TEXT,
    label: 'Current step',
    getCell: ({ stepName }: Engagement) => <StepCell stepName={stepName} />
  },
  {
    attribute: 'userRole',
    type: TEXT,
    label: 'Your role',
    getCell: ({roleAssignments}: Engagement) => <UserRoleCell roleAssignments={roleAssignments || []}/>,
  },
  {
    attribute: 'lastActivity',
    type: DATETIME,
    label: 'Last activity',
  },
];

export const TASKS = 'TASKS';
export const ENGAGEMENTS = 'ENGAGEMENTS';
export const ALL = 'ALL';
export const FOR_USER = 'FOR_USER';
export const INCOMPLETE = 'INCOMPLETE';

export const RESOURCE_DETAILS_BY_RESOURCE_TYPE: Record<ResourceType, ResourceDetails> = {
  [TASKS]: {
    resourceType: TASKS,
    label: 'Tasks',
    buildColumns: (args = {}) => TASK_COLUMNS,
    urlResource: 'tasks',
    buildPostUrl: ({
      engagementId,
      taskId,
    }: any) => `/automatic/engagements/${engagementId}/tasks/${taskId}/complete/`,
    filters: [
      {
        label: 'For user',
        value: FOR_USER,
      },
      {
        label: 'Incomplete',
        value: INCOMPLETE,
      },
    ],
  },
  [ENGAGEMENTS]: { // only available for workflow-level views
    resourceType: ENGAGEMENTS,
    label: 'Engagements',
    buildColumns: ({
      fields
    }: any) => [
      ...ENGAGEMENT_COLUMNS,
      ...fields.map((field: any) => ({
        attribute: `record.${toCamelCase(field.slug)}`,
        type: field.type,
        label: field.name
      }))
    ],
    urlResource: 'engagements',
    buildPostUrl: ({
      workflowId
    }: any) => `/automatic/workflows/${workflowId}/engagements/`,
    buildRoute: ({ engagementId }: { engagementId: string }) => `/engagements/${engagementId}/`,
    filters: [
      {
        label: 'For user',
        value: FOR_USER,
      },
      {
        label: 'Incomplete',
        value: INCOMPLETE,
      },
    ],
  },
};

export const RESOURCE_TYPE_OPTIONS = Object.keys(RESOURCE_DETAILS_BY_RESOURCE_TYPE).map((resourceType) => ({
  // @ts-ignore
  label: RESOURCE_DETAILS_BY_RESOURCE_TYPE[resourceType].label,
  value: resourceType
}));

export const ENGAGEMENT_RESOURCE_TYPE_OPTIONS = RESOURCE_TYPE_OPTIONS.filter((option) => option.value !== ENGAGEMENTS);
